// React Query hooks, one per miniapp-api endpoint. Query keys include the
// session chatId so cached data is scoped per chat. Mutations invalidate the
// home/relevant queries so meters and feeds reflect server state after actions.
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";

import { getSessionChatId } from "../auth/session";
import { api } from "./client";
import type {
  AnswerResult,
  CleanResult,
  FeedResult,
  GeoConsentResult,
  GeoMapView,
  GeoStatusView,
  HomeView,
  LeaderboardCategory,
  LeaderboardScope,
  LeaderboardView,
  ProfileView,
  QuestActionResult,
  QuestStartResult,
  QuestView,
  ReviveResult,
  RiddleView,
  SetupView,
  UserChatsView,
} from "./types";

function chatKey(...parts: unknown[]): unknown[] {
  return ["chat", getSessionChatId(), ...parts];
}

export const queryKeys = {
  home: () => chatKey("home"),
  riddle: () => chatKey("riddle"),
  quest: () => chatKey("quest"),
  setup: () => chatKey("setup"),
  me: () => chatKey("me"),
  leaderboard: (scope: LeaderboardScope, category: LeaderboardCategory) =>
    chatKey("leaderboard", scope, category),
  // Not chat-scoped: the DM chat picker is independent of any selected chat.
  userChats: () => ["userChats"] as const,
  // Not chat-scoped: geo consent and the world map are per-user, cross-chat.
  geoStatus: () => ["geo", "status"] as const,
  geoMap: () => ["geo", "map"] as const,
};

// --- Queries ---

export function useHome(options?: Partial<UseQueryOptions<HomeView, Error>>) {
  return useQuery<HomeView, Error>({
    queryKey: queryKeys.home(),
    queryFn: () => api.request<HomeView>(api.chatPath("/home")),
    // Meters decay server-side; poll modestly so Home stays roughly current.
    refetchInterval: 60_000,
    ...options,
  });
}

export function useRiddle(enabled = true) {
  return useQuery<RiddleView, Error>({
    queryKey: queryKeys.riddle(),
    queryFn: () => api.request<RiddleView>(api.chatPath("/riddle")),
    enabled,
  });
}

export function useQuest() {
  return useQuery<QuestView, Error>({
    queryKey: queryKeys.quest(),
    queryFn: () => api.request<QuestView>(api.chatPath("/quest")),
  });
}

export function useSetup(enabled = true) {
  return useQuery<SetupView, Error>({
    queryKey: queryKeys.setup(),
    queryFn: () => api.request<SetupView>(api.chatPath("/setup")),
    enabled,
  });
}

export function useLeaderboard(scope: LeaderboardScope, category: LeaderboardCategory) {
  return useQuery<LeaderboardView, Error>({
    queryKey: queryKeys.leaderboard(scope, category),
    queryFn: () =>
      api.request<LeaderboardView>(
        `/leaderboard?scope=${scope}&category=${category}`,
        // chat scope needs X-Chat-Id; global/chats ignore it harmlessly.
        { withChatId: scope === "chat" }
      ),
  });
}

export function useMe() {
  return useQuery<ProfileView, Error>({
    queryKey: queryKeys.me(),
    queryFn: () => api.request<ProfileView>("/users/me"),
  });
}

/**
 * DM "choose a chat" picker. Session-only route (no X-Chat-Id), so it works
 * before any chat is selected. Returns the viewer's joined chats plus a
 * discover list of popular public chats.
 */
export function useUserChats() {
  return useQuery<UserChatsView, Error>({
    queryKey: queryKeys.userChats(),
    queryFn: () => api.request<UserChatsView>("/users/me/chats", { withChatId: false }),
  });
}

/** Geo consent status + IP-detected location. Session-only route (no X-Chat-Id). */
export function useGeoStatus() {
  return useQuery<GeoStatusView, Error>({
    queryKey: queryKeys.geoStatus(),
    queryFn: () => api.request<GeoStatusView>("/geo/status", { withChatId: false }),
    staleTime: 30_000,
  });
}

/** World map payload (pins, stats, boards, online). Session-only route. */
export function useGeoMap() {
  return useQuery<GeoMapView, Error>({
    queryKey: queryKeys.geoMap(),
    queryFn: () => api.request<GeoMapView>("/geo/map", { withChatId: false }),
    // The server caches the map payload and the online count for 60s each;
    // polling faster would only re-read the same cache.
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

// --- Mutations ---

export function useFeed() {
  const qc = useQueryClient();
  return useMutation<FeedResult, Error, void>({
    mutationFn: () => api.request<FeedResult>(api.chatPath("/feed"), { method: "POST", body: {} }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.home() }),
  });
}

export function useClean() {
  const qc = useQueryClient();
  return useMutation<CleanResult, Error, void>({
    mutationFn: () => api.request<CleanResult>(api.chatPath("/clean"), { method: "POST", body: {} }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.home() }),
  });
}

export function useRevive() {
  const qc = useQueryClient();
  return useMutation<ReviveResult, Error, void>({
    mutationFn: () => api.request<ReviveResult>(api.chatPath("/revive"), { method: "POST", body: {} }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.home() }),
  });
}

export function useAnswerRiddle() {
  const qc = useQueryClient();
  return useMutation<AnswerResult, Error, { guess: string }>({
    mutationFn: ({ guess }) =>
      api.request<AnswerResult>(api.chatPath("/riddle/answer"), { method: "POST", body: { guess } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.riddle() });
      void qc.invalidateQueries({ queryKey: queryKeys.home() });
    },
  });
}

export function useStartQuest() {
  const qc = useQueryClient();
  return useMutation<QuestStartResult, Error, void>({
    mutationFn: () => api.request<QuestStartResult>(api.chatPath("/quest"), { method: "POST", body: {} }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.quest() }),
  });
}

export function useQuestAction() {
  const qc = useQueryClient();
  return useMutation<QuestActionResult, Error, { action: string }>({
    mutationFn: ({ action }) =>
      api.request<QuestActionResult>(api.chatPath("/quest/action"), {
        method: "POST",
        body: { action },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.quest() });
      void qc.invalidateQueries({ queryKey: queryKeys.home() });
    },
  });
}

export function useGeoConsent() {
  const qc = useQueryClient();
  return useMutation<GeoConsentResult, Error, void>({
    // The body is ignored server-side (anti-forgery): the pin always comes from
    // the server-observed IP, so there is nothing to send.
    mutationFn: () =>
      api.request<GeoConsentResult>("/geo/consent", {
        method: "POST",
        body: {},
        withChatId: false,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.geoStatus() });
      void qc.invalidateQueries({ queryKey: queryKeys.geoMap() });
    },
  });
}

export function useGeoHide() {
  const qc = useQueryClient();
  return useMutation<GeoConsentResult, Error, void>({
    mutationFn: () =>
      api.request<GeoConsentResult>("/geo/hide", {
        method: "POST",
        body: {},
        withChatId: false,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.geoStatus() });
      void qc.invalidateQueries({ queryKey: queryKeys.geoMap() });
    },
  });
}

export function useCompleteSetup() {
  const qc = useQueryClient();
  return useMutation<SetupView, Error, { language: string }>({
    mutationFn: ({ language }) =>
      api.request<SetupView>(api.chatPath("/setup"), { method: "POST", body: { language } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.setup() });
      void qc.invalidateQueries({ queryKey: queryKeys.home() });
    },
  });
}
